import React, { useMemo, useState } from 'react';

import { CaretDownOutlined } from '@ant-design/icons';
import { Dropdown, Empty, Input, Tree } from 'antd';
import type { AntTreeNodeProps, DataNode } from 'antd/es/tree';

import { ClearIcon } from '@/shared/assets/icons/input/ClearIcon';
import { COLOR } from '@/shared/design-tokens/design-tokens';
import { highlightText } from '@/shared/lib/text';
import { Icon } from '@/shared/ui/Icon';

import { useFilterStore } from '@/entities/discovery';
import type { DomainItem } from '@/entities/domain';
import { useSearchStore } from '@/entities/search';

import { useDomains } from '@/features/domain/model/useDomains';

import styles from './DomainDropdown.module.css';
import { buildDomainTree, type DomainTreeNode } from './domainTreeUtils';

const flattenOptions = (options: DomainTreeNode[]): DomainTreeNode[] => {
  return options.flatMap((opt) => [opt, ...(opt.children ? flattenOptions(opt.children) : [])]);
};

/**
 * aggregation에서 반환된 name과 매칭되는 모든 도메인을 찾습니다.
 * name은 displayName보다 유니크하지만, 여전히 중복 가능성이 있으므로 배열로 반환합니다.
 */
const findDomainsByName = (name: string, allDomains: DomainItem[]): DomainItem[] => {
  return allDomains.filter((d) => d.name === name);
};

/**
 * 주어진 도메인의 모든 조상(부모, 조부모 등)을 찾습니다.
 */
const findAllAncestors = (domain: DomainItem, allDomains: DomainItem[]): DomainItem[] => {
  const ancestors: DomainItem[] = [];
  let current = domain;

  while (current.parent) {
    const parent = allDomains.find((d) => d.id === current.parent?.id);
    if (!parent) break;

    ancestors.push(parent);
    current = parent;
  }

  return ancestors;
};

/**
 * 데이터가 있는 도메인과 그 조상들만 필터링합니다.
 */
const filterDomainsWithData = (
  allDomains: DomainItem[],
  domainAggregations: Array<{ key: string; doc_count: number }> | null
): DomainItem[] => {
  if (!domainAggregations || domainAggregations.length === 0) {
    return allDomains;
  }

  const domainsToInclude = new Set<string>();

  domainAggregations
    .filter((agg) => agg.doc_count > 0)
    .forEach((agg) => {
      const matchingDomains = findDomainsByName(agg.key, allDomains);

      matchingDomains.forEach((domain) => {
        domainsToInclude.add(domain.id);

        const ancestors = findAllAncestors(domain, allDomains);
        ancestors.forEach((ancestor) => domainsToInclude.add(ancestor.id));
      });
    });

  return allDomains.filter((domain) => domainsToInclude.has(domain.id));
};

export const DomainDropdown: React.FC = () => {
  const { filters } = useFilterStore();
  const [searchQuery, setSearchQuery] = useState('');
  const [open, setOpen] = useState(false);

  const { data: domainsData, isLoading: isLoadingDomains } = useDomains();
  const { domainAggregations } = useSearchStore();

  const domainOptions = useMemo(() => {
    const allDomains = domainsData?.data || [];
    if (allDomains.length === 0) return [];

    const filteredDomains = filterDomainsWithData(allDomains, domainAggregations);
    return buildDomainTree(filteredDomains);
  }, [domainsData?.data, domainAggregations]);

  const flattenedOptions = useMemo(() => flattenOptions(domainOptions), [domainOptions]);

  const valueToKeyMap = useMemo(() => new Map(flattenedOptions.map((opt) => [opt.value, opt.key])), [flattenedOptions]);

  const keyToValueMap = useMemo(() => new Map(flattenedOptions.map((opt) => [opt.key, opt.value])), [flattenedOptions]);

  const domainsWithData = useMemo(() => {
    if (!domainAggregations) return new Set<string>();
    return new Set(domainAggregations.filter((agg) => agg.doc_count > 0).map((agg) => agg.key));
  }, [domainAggregations]);

  const selectedKeys = useMemo(() => {
    if (!filters.domain || filters.domain.length === 0) return [];
    return filters.domain.map((name) => valueToKeyMap.get(name) || name);
  }, [filters.domain, valueToKeyMap]);

  const filteredOptions = useMemo(() => {
    if (!searchQuery) return domainOptions;

    const filterTree = (options: DomainTreeNode[]): DomainTreeNode[] => {
      return options
        .map((option) => {
          const matchesTitle = option.title.toLowerCase().includes(searchQuery.toLowerCase());
          const filteredChildren = option.children ? filterTree(option.children) : [];

          if (matchesTitle || filteredChildren.length > 0) {
            return {
              ...option,
              children: filteredChildren.length > 0 ? filteredChildren : option.children,
            };
          }
          return null;
        })
        .filter(Boolean) as DomainTreeNode[];
    };

    return filterTree(domainOptions);
  }, [searchQuery, domainOptions]);

  const treeData = useMemo(() => {
    const convertToTreeData = (options: DomainTreeNode[]): DataNode[] => {
      return options.map((option) => {
        const hasData = domainsWithData.has(option.value);
        return {
          title: (
            <div className={styles.treeNodeTitle}>
              <span className={styles.nodeLabel}>
                {highlightText(option.title, searchQuery, { className: 'highlight' })}
              </span>
            </div>
          ),
          key: option.key,
          disabled: !hasData,
          children: option.children ? convertToTreeData(option.children) : [],
        };
      });
    };
    return convertToTreeData(filteredOptions);
  }, [filteredOptions, searchQuery, domainsWithData]);

  const handleCheck = (checkedKeys: React.Key[] | { checked: React.Key[]; halfChecked: React.Key[] }) => {
    const keys = Array.isArray(checkedKeys) ? checkedKeys : checkedKeys.checked;
    const domainNames = keys.map((key) => keyToValueMap.get(String(key)) || String(key));
    useFilterStore.getState().updateDomainFilter(domainNames);
  };

  const handleSelect = (_keys: React.Key[], info: { node: { key: React.Key } }) => {
    const clickedKey = String(info.node.key);
    const clickedValue = keyToValueMap.get(clickedKey) || clickedKey;

    const currentValues = filters.domain || [];
    const newValues = currentValues.includes(clickedValue)
      ? currentValues.filter((v) => v !== clickedValue)
      : [...currentValues, clickedValue];

    useFilterStore.getState().updateDomainFilter(newValues);
  };

  const handleClear = () => {
    useFilterStore.getState().updateDomainFilter([]);
  };

  const handleOpenChange = (open: boolean) => {
    setOpen(open);
    if (!open) {
      setSearchQuery('');
    }
  };

  const buttonText = useMemo(() => {
    if (selectedKeys.length === 0) return '전체';

    if (selectedKeys.length === 1) {
      const firstKey = selectedKeys[0];
      const firstOption = flattenedOptions.find((opt) => opt.key === firstKey);
      return firstOption?.title || '전체';
    }

    const firstKey = selectedKeys[0];
    const firstOption = flattenedOptions.find((opt) => opt.key === firstKey);
    const firstLabel = firstOption?.title || selectedKeys[0];
    const remainingCount = selectedKeys.length - 1;

    return `${firstLabel}, ... +${remainingCount}`;
  }, [selectedKeys, flattenedOptions]);

  const switcherIcon = (nodeProps: AntTreeNodeProps) => {
    const { expanded } = nodeProps;
    return expanded ? (
      <Icon name="tree-collapse" size={16} color={COLOR.GRAY[0]} />
    ) : (
      <Icon name="tree-expand" size={16} color={COLOR.GRAY[0]} />
    );
  };

  const dropdownContent = (
    <div className={styles.dropdownContent}>
      <div className={styles.searchSection}>
        <Input
          prefix={<Icon name="search" size={13} color={COLOR.GRAY[0]} />}
          placeholder="도메인 검색..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          maxLength={256}
          allowClear={{
            clearIcon: <ClearIcon size={14} backgroundColor={COLOR.GRAY[80]} iconColor={COLOR.GRAY[100]} />,
          }}
          className={styles.searchInput}
        />
      </div>

      {treeData.length > 0 ? (
        <div className={styles.treeSection}>
          <Tree
            checkable
            checkStrictly
            defaultExpandAll
            checkedKeys={selectedKeys}
            selectedKeys={[]}
            onCheck={handleCheck}
            onSelect={handleSelect}
            treeData={treeData}
            switcherIcon={switcherIcon}
            className={styles.tree}
          />
        </div>
      ) : (
        <div className={styles.emptyWrapper}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={searchQuery ? `"${searchQuery}"에 대한 검색 결과가 없습니다` : '검색 결과가 없습니다'}
          />
        </div>
      )}

      <div className={styles.actions}>
        <button
          type="button"
          className={`${styles.clearButton} ${selectedKeys.length === 0 ? styles.inactive : ''}`}
          onClick={handleClear}>
          선택 해제
        </button>
      </div>
    </div>
  );

  return (
    <Dropdown
      open={open}
      onOpenChange={handleOpenChange}
      popupRender={() => dropdownContent}
      trigger={['click']}
      placement="bottomLeft"
      align={{ offset: [0, 8] }}
      disabled={isLoadingDomains}>
      <button
        type="button"
        className={`${styles.filterButton} ${isLoadingDomains ? styles.disabled : ''}`}
        onClick={() => setOpen(!open)}
        disabled={isLoadingDomains}>
        <div className={styles.buttonContent}>
          <span className={styles.buttonLabel}>도메인:</span>
          <span className={styles.buttonValue}>{buttonText}</span>
        </div>
        <span className={styles.iconWrapper}>
          <CaretDownOutlined className={styles.dropdownIcon} />
        </span>
      </button>
    </Dropdown>
  );
};
